import React, { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Table, Input, DatePicker, Form, Row, Col, Button, message } from 'antd';
import { createCrew } from '../../services/auth';
import dayjs from 'dayjs';

const data = [
  { key: 1, name: 'John Smith', id: 'E1001', union: 'Local 12', ssn4: '1234', task: 'Welding', trade: 'Welder' },
  { key: 2, name: 'Sarah Johnson', id: 'E1002', union: 'Local 10', ssn4: '5678', task: 'Plumbing', trade: 'Plumber' },
  { key: 3, name: 'David Lee', id: 'E1003', union: 'Local 12', ssn4: '9012', task: 'Carpentry', trade: 'Carpenter' },
  { key: 4, name: 'Maria Garcia', id: 'E1004', union: 'Local 13', ssn4: '1122', task: 'Electrical', trade: 'Electrician' },
  { key: 5, name: 'Ahmed Khan', id: 'E1005', union: 'Local 16', ssn4: '3344', task: 'HVAC', trade: 'Technician' },
  { key: 6, name: 'Emily Davis', id: 'E1006', union: 'Local 18', ssn4: '7788', task: 'Welding', trade: 'Welder' },
  { key: 7, name: 'Michael Brown', id: 'E1007', union: 'Local 20', ssn4: '9900', task: 'Carpentry', trade: 'Carpenter' },
  { key: 8, name: 'Rachel Green', id: 'E1008', union: 'Local 15', ssn4: '2244', task: 'Painting', trade: 'Painter' },
  { key: 9, name: 'Chris Evans', id: 'E1009', union: 'Local 18', ssn4: '5566', task: 'Plumbing', trade: 'Plumber' },
  { key: 10, name: 'Olivia Wilson', id: 'E1010', union: 'Local 09', ssn4: '7788', task: 'Electrical', trade: 'Electrician' },
  { key: 11, name: 'Ethan Hall', id: 'E1011', union: 'Local 22', ssn4: '9911', task: 'Carpentry', trade: 'Carpenter' },
  { key: 12, name: 'Isabella Martinez', id: 'E1012', union: 'Local 12', ssn4: '3322', task: 'Welding', trade: 'Welder' },
  { key: 13, name: 'Liam Thomas', id: 'E1013', union: 'Local 26', ssn4: '4433', task: 'HVAC', trade: 'Technician' },
];

const AssignEmployee = forwardRef(({ basicDetails, initialEmployees = [], initialDate, onPrev, onNext }, ref) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState(
    initialEmployees.map(emp => emp.key)
  );
  const [form] = Form.useForm();
  const [nameFilter, setNameFilter] = useState('');
  const [ssn4Filter, setSsn4Filter] = useState('');
  const [taskFilter, setTaskFilter] = useState('');

  useEffect(() => {
    if (initialDate) {
      let dateValue;
      if (dayjs.isDayjs(initialDate)) {
        dateValue = initialDate;
      } else if (typeof initialDate === 'string') {
        dateValue = dayjs(initialDate, 'YYYY-MM-DD', true);
        if (!dateValue.isValid()) {
          dateValue = dayjs(initialDate, 'DD-MM-YYYY', true);
        }
      }
      if (dateValue && dateValue.isValid()) {
        form.setFieldsValue({ date: dateValue });
      }
    }
  }, [initialDate]);

  const columns = [
    { title: 'Sl. no.', dataIndex: 'key', key: 'key', width: 70 },
    {
      title: 'Employee Name',
      dataIndex: 'name',
      key: 'name',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div>
          <Input
            placeholder="Search Name"
            value={selectedKeys[0] || ''}
            onChange={e => {
              const val = e.target.value;
              setSelectedKeys(val ? [val] : []);
              setNameFilter(val);
            }}
            style={{ width: 120, marginBottom: 8, display: 'block' }}
          />
          <Button
            onClick={() => {
              confirm();
            }}
            size="small"
            type="primary"
            style={{ width: '49%', marginRight: '2%' }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setNameFilter('');
              confirm();
            }}
            size="small"
            style={{ width: '49%' }}
          >
            Reset
          </Button>
        </div>
      ),
      filterIcon: () => <span role="img" aria-label="search">🔍</span>,
      onFilter: (value, record) =>
        !value || record.name.toLowerCase().includes(value.toLowerCase()),
      filteredValue: nameFilter ? [nameFilter] : [],
      width: 150,
    },
    {
      title: 'Employee ID',
      dataIndex: 'id',
      key: 'id',
      width: 120,
    },
    {
      title: 'Union',
      dataIndex: 'union',
      key: 'union',
      width: 120,
    },
    {
      title: 'SSN4',
      dataIndex: 'ssn4',
      key: 'ssn4',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div>
          <Input
            placeholder="Search SSN4"
            value={selectedKeys[0] || ''}
            onChange={e => {
              const val = e.target.value;
              setSelectedKeys(val ? [val] : []);
              setSsn4Filter(val);
            }}
            style={{ width: 100, marginBottom: 8, display: 'block' }}
          />
          <Button
            onClick={() => {
              confirm();
            }}
            size="small"
            type="primary"
            style={{ width: '49%', marginRight: '2%' }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setSsn4Filter('');
              confirm();
            }}
            size="small"
            style={{ width: '49%' }}
          >
            Reset
          </Button>
        </div>
      ),
      filterIcon: () => <span role="img" aria-label="search">🔍</span>,
      onFilter: (value, record) =>
        !value || record.ssn4.toLowerCase().includes(value.toLowerCase()),
      filteredValue: ssn4Filter ? [ssn4Filter] : [],
      width: 100,
    },
    {
      title: 'Task',
      dataIndex: 'task',
      key: 'task',
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
        <div>
          <Input
            placeholder="Search Task"
            value={selectedKeys[0] || ''}
            onChange={e => {
              const val = e.target.value;
              setSelectedKeys(val ? [val] : []);
              setTaskFilter(val);
            }}
            style={{ width: 120, marginBottom: 8, display: 'block' }}
          />
          <Button
            onClick={() => {
              confirm();
            }}
            size="small"
            type="primary"
            style={{ width: '49%', marginRight: '2%' }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              setTaskFilter('');
              confirm();
            }}
            size="small"
            style={{ width: '49%' }}
          >
            Reset
          </Button>
        </div>
      ),
      filterIcon: () => <span role="img" aria-label="search">🔍</span>,
      onFilter: (value, record) =>
        !value || record.task.toLowerCase().includes(value.toLowerCase()),
      filteredValue: taskFilter ? [taskFilter] : [],
      width: 120,
    },
    { title: 'Trade Class/Skill', dataIndex: 'trade', key: 'trade', width: 150 },
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys) => setSelectedRowKeys(selectedKeys),
  };

  // Expose method to parent to get selected employees and validate
  useImperativeHandle(ref, () => ({
    getSelectedEmployees: () => {
      const selectedEmployees = data.filter(emp => selectedRowKeys.includes(emp.key));
      const date = form.getFieldValue('date');
      if (selectedEmployees.length === 0) {
        message.warning('Please select at least one employee.');
        return { valid: false, employees: [], date: null };
      }
      if (!date) {
        message.warning('Please select a date.');
        return { valid: false, employees: [], date: null };
      }
      return { valid: true, employees: selectedEmployees, date };
    }
  }));

  // On Next, validate and pass data up
  const handleNext = () => {
    if (onNext) {
      const selectedEmployees = data.filter(emp => selectedRowKeys.includes(emp.key));
      const date = form.getFieldValue('date');
      if (selectedEmployees.length === 0) {
        message.warning('Please select at least one employee.');
        return;
      }
      if (!date) {
        message.warning('Please select a date.');
        return;
      }
      onNext({ employees: selectedEmployees, date });
    }
    };
    

  
    

  return (
    <Form layout="vertical" form={form}>
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col span={6}>
          <Form.Item label="Date" name="date" rules={[{ required: true, message: 'Date is required' }]}>
            <DatePicker style={{ width: '100%' }} format="DD-MM-YYYY" />
          </Form.Item>
        </Col>
      </Row>
      <Table
        columns={columns}
        dataSource={data}
        pagination={false}
        scroll={{ x: 'max-content' }}
        bordered
        rowSelection={rowSelection}
      />
      <Row justify="end" style={{ marginTop: 16 }}>
  <Col>
    <Button style={{ marginRight: 8 }} onClick={onPrev}>
      Previous
    </Button>
  </Col>
  <Col>
    <Button type="primary" onClick={handleNext}>
      Next
    </Button>
  </Col>
</Row>

    </Form>
  );
});

export default AssignEmployee;

